import { Box, Button, Menu, MenuButton, MenuItem, MenuList, useTheme, Text, Image, ImageProps } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { CaretDown } from 'components/Icons/Icons';
import { lighten } from 'polished';
import { rem } from 'utils/fonts';
import React, { FC } from 'react';
import { mq } from 'styles/breakpoints';
import { useTranslation } from 'react-i18next';
import BritishFlag from 'assets/svgs/flag-en.svg';
import PolishFlag from 'assets/svgs/flag-pl.svg';
import JapaneseFlag from 'assets/svgs/flag-ja.svg';
import PortugeseFlag from 'assets/flag-por.png';
import SpanishFlag from 'assets/flag-spa.png';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { translateProjectsAndPortfolios } from 'modules/projects/projectsSlice';
import { updateLanguage } from 'modules/user/userSlice';
import { getLanguages } from '../corporate/shared/getLanguages';

const StyledBox = styled(Box)`
  ${mq.xl} {
    margin-right: 20px;
  }
`;

const StyledMenuItem = styled(MenuItem)<{ color?: string; selected?: boolean }>`
  height: ${(props): string => (props.color ? '64px' : '50px')};
  position: relative;
  white-space: nowrap;
`;

const StyledMenuList = styled(MenuList)`
  padding: 0;
  overflow: hidden;
`;

const Overlay = styled.div`
  content: '';
  cursor: pointer;
  position: absolute;
  z-index: 999;
  background: transparent;
  height: 58px;
  left: -10px;
  top: 0;
  right: -10px;
`;

const Flag: FC<{ lang: string; alt: string } & ImageProps> = ({ lang, ...props }) => {
  let flag;

  switch (lang) {
    case 'en':
      flag = BritishFlag;
      break;
    case 'en-UK':
      flag = BritishFlag;
      break;
    case 'pl':
      flag = PolishFlag;
      break;
    case 'es':
      flag = SpanishFlag;
      break;
    case 'pt':
      flag = PortugeseFlag;
      break;
    case 'ja':
      flag = JapaneseFlag;
      break;
    default:
      flag = BritishFlag;
  }

  return <Image boxSize="30px" objectFit="contain" src={flag} {...props} />;
};

const LanguageSwitcher: FC = () => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation('common');
  const languages = useSelector((state: RootState) => state.app.languages);
  const dispatch = useDispatch();

  const onMenuItemClick = (lang: string): void => {
    i18n.changeLanguage(lang);
    dispatch(translateProjectsAndPortfolios(lang));
    dispatch(updateLanguage(lang));
  };

  const preventFocus = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    if (document.activeElement) {
      (document.activeElement as HTMLDivElement).blur();
    }
  };

  const prevent = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
  };

  return (
    <StyledBox>
      <Menu autoSelect={false} placement="bottom-end">
        {({ isOpen }): React.ReactNode => (
          <>
            <MenuButton
              rightIcon={<CaretDown />}
              onClick={prevent}
              bgColor={colors.white}
              as={Button}
              w="70px"
              fontFamily="body"
              border="none"
              m={{ base: '0 0 0 5px', lg: '0 0 0 30px' }}
              sx={{
                height: '45px',
                justifyContent: 'flex-start',
                padding: '0 10px',
                _hover: {
                  bgColor: colors.white,
                },

                '& > span:last-of-type svg': {
                  fontSize: rem(20),
                  marginLeft: '5px',
                  color: colors.secondary[400],
                },
              }}
            >
              <Flag
                lang={getLanguages(languages).find(lang => lang?.value === i18n.language)?.value ?? 'en'}
                alt={`${t('languages.selectedLanguage')} ${'en'}`}
              />
            </MenuButton>
            <StyledMenuList w="180px" boxShadow="menuList" bg={colors.white} borderRadius="medium">
              {getLanguages(languages).map((item: LanguageItem | undefined) => {
                if (!item) {
                  return null;
                }
                const selected = item.value === i18n.language;
                const color = selected ? colors.primary[500] : colors.primary[100];
                return (
                  <StyledMenuItem
                    id={`${item.value} chooose`}
                    selected={selected}
                    color={color}
                    fontSize="md"
                    bg={selected ? colors.primary[500] : 'transparent'}
                    _hover={{
                      background: selected ? lighten(0.1, color) : color,
                    }}
                    key={item.value}
                    onClick={() => onMenuItemClick(item.value)}
                    display="flex"
                    align="center"
                  >
                    <Flag lang={item.value} mr="10px" alt={t('languages.flagAltText')} />
                    <Text
                      fontFamily="heading"
                      color={selected ? colors.other.primaryBgText : colors.secondary[900]}
                      fontSize="md"
                      textTransform={'capitalize'}
                    >
                      {item?.label}
                    </Text>
                  </StyledMenuItem>
                );
              })}
            </StyledMenuList>
            {isOpen && <Overlay onClick={preventFocus} />}
          </>
        )}
      </Menu>
    </StyledBox>
  );
};

export default LanguageSwitcher;
